import React from 'react';
import { CheckCircle2, XCircle, Lightbulb } from 'lucide-react';

/**
 * articleKit — shared, decent presentational primitives for language articles.
 * Calm editorial design: neutral surfaces, a single amber accent (the
 * Languages section identity). Reused across every JavaScript topic so the
 * pages stay visually consistent.
 */

export const SectionHeading: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
 <div className={`flex items-center gap-3 mb-5 ${n === 1 ? '' : 'mt-10 pt-6 border-t border-gray-200 dark:border-gray-700'}`}>
  <span className="article-section-number article-number-badge flex items-center justify-center w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-bold">{n}</span>
  <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{children}</h2>
 </div>
);

export const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-6">{children}</h3>
);

export const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <p className="text-[15px] leading-7 text-gray-700 dark:text-gray-300 mb-4">{children}</p>
);

export const Mark: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <span className="font-semibold text-gray-900 dark:text-white">{children}</span>
);

export const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[13px] font-mono text-amber-700 dark:text-amber-300">{children}</code>
);

export const Callout: React.FC<{ tone: 'tip' | 'warning' | 'info'; title?: string; children: React.ReactNode }> = ({ tone, title, children }) => {
 const map = {
  tip: { border: 'border-green-400', bg: 'bg-green-50/60 dark:bg-green-900/10', icon: <CheckCircle2 className="w-5 h-5 text-green-500" /> },
  warning: { border: 'border-red-400', bg: 'bg-red-50/60 dark:bg-red-900/10', icon: <XCircle className="w-5 h-5 text-red-500" /> },
  info: { border: 'border-amber-400', bg: 'bg-amber-50/60 dark:bg-amber-900/10', icon: <Lightbulb className="w-5 h-5 text-amber-500" /> },
 }[tone];
 return (
  <div className={`flex items-start gap-3 pl-4 pr-4 py-3 border-l-2 rounded-r-lg ${map.border} ${map.bg} mb-4`}>
   <span className="mt-0.5 flex-shrink-0">{map.icon}</span>
   <div className="text-sm leading-6 text-gray-700 dark:text-gray-300">
    {title && <span className="font-semibold text-gray-900 dark:text-white">{title}: </span>}
    {children}
   </div>
  </div>
 );
};

export const Output: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
  <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">Output</div>
  <pre className="px-4 py-3 bg-gray-900 text-green-300 text-sm font-mono whitespace-pre-wrap">{children}</pre>
 </div>
);

export const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
 <span className="px-2.5 py-1 rounded-md text-[12px] font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">{children}</span>
);

// A conceptual memory cell: variable name label under a boxed value.
export const MemoryBox: React.FC<{ name: string; value: string; tone?: 'neutral' | 'amber' }> = ({ name, value, tone = 'neutral' }) => (
 <div className="inline-flex flex-col items-center gap-1.5">
  <div className={`min-w-[120px] px-5 py-3 rounded-lg border-2 text-center font-mono text-sm font-semibold ${tone === 'amber' ? 'border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200'}`}>
   {value}
  </div>
  <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400">↑ {name}</span>
 </div>
);

// Two-column comparison block (e.g., null vs undefined, let vs const).
export const TwoUp: React.FC<{ left: { title: string; items: string[] }; right: { title: string; items: string[] } }> = ({ left, right }) => (
 <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
  {[left, right].map((col) => (
   <div key={col.title} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
    <div className="text-sm font-bold text-gray-900 dark:text-white mb-2">{col.title}</div>
    <ul className="space-y-1.5">
     {col.items.map((it, i) => (
      <li key={i} className="flex items-start gap-2 text-[13px] text-gray-600 dark:text-gray-300">
       <span className="text-amber-500 mt-0.5">•</span><span>{it}</span>
      </li>
     ))}
    </ul>
   </div>
  ))}
 </div>
);

// An interview question + answer block.
export const QA: React.FC<{ q: React.ReactNode; children: React.ReactNode }> = ({ q, children }) => (
 <div className="mb-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
  <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-start gap-2">
   <span className="text-amber-600 dark:text-amber-400 font-bold text-sm flex-shrink-0">Q</span>
   <span className="text-[14px] font-semibold text-gray-800 dark:text-gray-200">{q}</span>
  </div>
  <div className="px-4 py-3 flex items-start gap-2">
   <span className="text-green-600 dark:text-green-400 font-bold text-sm flex-shrink-0">A</span>
   <div className="text-[14px] leading-6 text-gray-700 dark:text-gray-300">{children}</div>
  </div>
 </div>
);
